import { createI18n } from 'vue-i18n'

import { clientStorageLocal, LOCAL_STORAGE_LOCALE_KEY } from '@ao/utils'

function getRussianPluralRule(choice) {
	choice = Math.abs(choice)
	if (choice === 0) return 0
	if (choice % 10 === 1 && choice % 100 !== 11) return 1
	if (choice % 10 >= 2 && choice % 10 <= 4 && (choice % 100 < 12 || choice % 100 > 14)) return 2
	return 3
}

function getUkrainianPluralRule(choice) {
	choice = Math.abs(choice)
	if (choice === 0) return 0
	if (choice % 10 === 1 && choice % 100 !== 11) return 1
	if (choice % 10 >= 2 && choice % 10 <= 4 && (choice % 100 < 12 || choice % 100 > 14)) return 2
	return 3
}

function getBrowserLocale({ countryCodeOnly = false } = {}) {
	const navigatorLocale = navigator.languages?.[0] || navigator.language
	if (!navigatorLocale) return undefined
	return countryCodeOnly ? navigatorLocale.trim().split(/-|_/)[0] : navigatorLocale.trim()
}

/**
 * Асинхронно создает и настраивает плагин vue-i18n.
 * @param {object} config - Конфигурация
 * @param {object} config.appLocaleModules - Результат import.meta.glob для файлов локалей.
 * @param {string} config.fallbackLocale - Резервная локаль.
 * @param {object} config.supportedLocales - Объект с поддерживаемыми локалями.
 * @returns {Promise<import('vue').Plugin>} - Промис, который разрешается в готовый Vue плагин.
 */
export async function createAoI18n(config) {
	const {
		appLocaleModules,
		fallbackLocale = 'ru',
		supportedLocales,
	} = config

	if (!supportedLocales) {
		throw new Error('[AoI18n] supportedLocales must be provided in config.')
	}

	// --- Создаем сервис внутри фабрики ---
	const i18nService = (() => {
		const i18n = createI18n({
			legacy: false,
			locale: fallbackLocale,
			fallbackLocale,
			messages: {},
			pluralRules: {
				ru: getRussianPluralRule,
				uk: getUkrainianPluralRule,
			},
			warnHtmlInMessage: 'off',
		})

		function supportedLocalesInclude(locale) {
			return Object.keys(supportedLocales).includes(locale)
		}

		async function loadLocaleMessages(locale) {
			const modulePath = `../../src/assets/locales/${locale}.json`
			if (appLocaleModules[modulePath]) {
				const { default: messages } = await appLocaleModules[modulePath]()
				i18n.global.setLocaleMessage(locale, messages)
				return messages
			}
		}

		function getStartingLocale() {
			const urlParams = new URLSearchParams(window.location.search)
			const queryLocale = urlParams.get('locale')
			if (queryLocale && supportedLocalesInclude(queryLocale)) return queryLocale
			const savedLocale = clientStorageLocal.get(LOCAL_STORAGE_LOCALE_KEY)
			if (savedLocale) return savedLocale
			const browserLocale = getBrowserLocale({ countryCodeOnly: true })
			if (browserLocale && supportedLocalesInclude(browserLocale)) return browserLocale
			return fallbackLocale
		}

		async function setLocale(localeKey) {
			if (!i18n.global.availableLocales.includes(localeKey)) {
				await loadLocaleMessages(localeKey)
			}
			i18n.global.locale.value = localeKey
			clientStorageLocal.add(LOCAL_STORAGE_LOCALE_KEY, localeKey)
			document.querySelector('html').setAttribute('lang', localeKey)
		}

		// Возвращаем публичный API сервиса, который будет доступен через provide
		return {
			i18n,
			getStartingLocale,
			setLocale,
			supportedLocales,
		}
	})()

	// --- Загружаем стартовую локаль ---
	const startingLocale = i18nService.getStartingLocale()
	await i18nService.setLocale(startingLocale)

	// --- Возвращаем сам Vue плагин ---
	return {
		install(app) {
			// Подключаем сам инстанс i18n
			app.use(i18nService.i18n)
			// Предоставляем доступ к нашему сервису для всего приложения
			app.provide('i18nService', i18nService)
		},
	}
}
